import {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { LAYER_COLORS } from "../../constants/design/colors";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useToast } from "../../hooks/toast";
import { getUsdcDecimals } from "../../config/chains";
import { apiNum, calculatePnlPercent } from "../../utils/math";
import { toTokenUnits } from "../../utils/decimalString";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import BaseDetailsModal from "../shared/ViewDetailsModal/BaseDetailsModal";
import SegmentedControl from "../shared/SegmentedControl";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { BORDER_RADIUS, SPACING } from "../../constants/design/spacing";
import { Label, WarningLabel } from "../TradeForm/style";
import { Input } from "../shared/Input";
import { ButtonThemeEnum } from "../Buttons/styles";
import { SubmitButton } from "./style";
import { MaxButton } from "../shared/NumericField";
import { Spinner } from "../shared/Spinner";
import { IAdjustIsolatedMarginModalProps } from "./AdjustIsolatedMarginModal.types";
import AdjustIsolatedMarginModalDetails from "./AdjustIsolatedMarginModalDetails";
import {
  computeMarginLimits,
  marginAmountIssue,
} from "./AdjustIsolatedMarginModal.helpers";

/**
 * The signed margin change in USDC base units, as the integer text the API
 * takes, or `undefined` when the amount is not a positive decimal.
 *
 * `String(Number(amount) * 10 ** decimals)` wrote 8.2 USDC as
 * "8199999.999999999" and large amounts as "1.234e+21"; this floors the typed
 * text itself to the token's decimals.
 */
export const toMarginDeltaUnits = (
  amount: string | undefined,
  isAddMargin: boolean,
  decimals: number
): string | undefined => {
  const units = toTokenUnits(amount, decimals);
  if (!units || units.isZero()) return undefined;
  return (isAddMargin ? units : units.mul(-1)).toString();
};

function AdjustIsolatedMarginModal({
  position,
  onHide,
  style,
}: IAdjustIsolatedMarginModalProps) {
  const { updateIsolatedMargin } = useGetAccount();
  const { addToast, addErrorToast } = useToast();
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ViewDetailsModal.AdjustIsolatedMarginModal",
  });
  const { t: apiErrors } = useTranslation("apiErrors");

  const { data: accountData } = useGetAccount();
  const [, setError] = useState<string>();
  const [marginError, setMarginError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [isAddMargin, setIsAddMargin] = useState(true);
  // Kept as the typed text so the submitted units are not a float round trip.
  const [marginAmount, setMarginAmount] = useState<string | undefined>(
    undefined
  );
  const marginAmountNum =
    marginAmount === undefined ? undefined : Number(marginAmount);
  const { t: inputsT } = useTranslation("app", { keyPrefix: "common.inputs" });

  const onHideModal = useCallback(() => {
    onHide?.();
    setIsAddMargin(true);
    setMarginAmount(undefined);
    setMarginError(undefined);
  }, [onHide]);

  const onUpdateMargin = useCallback(async () => {
    const updatedMargin = toMarginDeltaUnits(
      marginAmount,
      isAddMargin,
      getUsdcDecimals()
    );
    if (!position || !updatedMargin) {
      return;
    }

    try {
      setError(undefined);
      setLoading(true);
      await updateIsolatedMargin(position.instrument_id, updatedMargin);
      onHideModal();
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: `Margin ${isAddMargin ? "Added" : "Removed"}`,
          subheader: "Your position margin was successfully adjusted",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    } catch (e: any) {
      addErrorToast(t("error_adjust_margin"), apiErrors(e));
      setError(apiErrors(e.message) || t("error_adjust_margin"));
    } finally {
      setLoading(false);
    }
  }, [
    addErrorToast,
    addToast,
    apiErrors,
    isAddMargin,
    marginAmount,
    onHideModal,
    position,
    t,
    updateIsolatedMargin,
  ]);

  const profitPercent = useMemo(() => {
    if (!position) {
      return undefined;
    }
    // Unknown PnL (null) has no ROI. `Number(null)` reported a flat 0%.
    const pnl = apiNum(position.unrealized_pnl);
    if (pnl === undefined) {
      return undefined;
    }
    const totalEntryValue =
      Number(position.avg_entry_price) * Number(position.amount);
    return calculatePnlPercent(
      pnl,
      totalEntryValue,
      Number(position.isolated_margin)
    );
  }, [position]);

  const accountLeverage = useMemo(
    () =>
      accountData?.leverages?.find(
        (lev) => lev.instrument_id === position?.instrument_id
      )?.leverage,
    [accountData?.leverages, position?.instrument_id]
  );

  // Free collateral to add; margin removable while the position keeps its
  // INITIAL margin (decision 4). Exact, and unknown — never 0 — when a term is
  // missing: see helpers.
  const { addMax, removeMax, marginBalance } = useMemo(
    () => computeMarginLimits(position, accountData, accountLeverage, getUsdcDecimals()),
    [accountData, accountLeverage, position]
  );
  const maxMargin = isAddMargin ? addMax : removeMax;
  const maxUnavailableReason = maxMargin.ok
    ? undefined
    : inputsT(`max_unavailable.${maxMargin.reason}`);

  const onMaxClick = useCallback(() => {
    // An unknown limit has no Max: the button is disabled, and this is a no-op.
    if (!maxMargin.ok) return;
    // Already floored to the cent by the resolver: 12.346 is 12.34, never 12.35.
    setMarginAmount(maxMargin.max);
  }, [maxMargin]);

  useEffect(() => {
    switch (marginAmountIssue(marginAmount, isAddMargin, { addMax, removeMax })) {
      case "insufficient_balance":
        setMarginError(t("insufficient_margin_balance"));
        break;
      case "balance_unknown":
        setMarginError(
          t("margin_balance_unknown", {
            defaultValue:
              "Available margin balance is unknown right now, so this amount can't be checked.",
          })
        );
        break;
      case "below_initial_margin":
        setMarginError(
          t("initial_margin_must_remain", {
            max: removeMax.ok ? removeMax.max : "—",
          })
        );
        break;
      case "remove_unknown":
        setMarginError(t("initial_margin_unknown"));
        break;
      default:
        setMarginError(undefined);
    }
  }, [addMax, isAddMargin, marginAmount, removeMax, t]);

  return (
    <BaseDetailsModal
      instrument={
        position
          ? {
              instrument_name: position.instrument_name,
              instrument_type: position.instrument_type,
              expiry: position.option?.expiry,
              strike: position.option?.strike,
              markPrice: position.mark_price,
            }
          : undefined
      }
      show={!!position}
      onHide={onHideModal}
      style={style}
    >
      <SegmentedControl
        segments={[
          {
            value: "1",
            display: t("add_margin"),
          },
          {
            value: "0",
            display: t("remove_margin"),
          },
        ]}
        value={isAddMargin ? "1" : "0"}
        onSelect={(v) => {
          setIsAddMargin(Boolean(Number(v)));
          setMarginAmount("0");
        }}
        config={{
          widthType: "fullWidth",
          backgroundColor: LAYER_COLORS.two,
          activeBackgroundColor: LAYER_COLORS.three,
          borderRadius: `${BORDER_RADIUS.three}px`,
          button: {
            fontSize: FONT_SIZE.one,
            py: SPACING.two,
          },
        }}
      />
      <Label style={{ marginTop: SPACING.three }}>{t("margin_amount")}</Label>
      <Input
        error={Boolean(marginError)}
        placeholder="0"
        type="number"
        value={marginAmount}
        onChange={(e) => setMarginAmount(e.target.value)}
        rightAccessory={
          <MaxButton
            onClick={onMaxClick}
            type="button"
            disabled={!maxMargin.ok}
            title={maxUnavailableReason}
            aria-label={maxUnavailableReason ? `${inputsT("max")}: ${maxUnavailableReason}` : undefined}
          >
            {inputsT("max")}
          </MaxButton>
        }
      />
      {marginError ? <WarningLabel>{marginError}</WarningLabel> : null}
      {position && (
        <AdjustIsolatedMarginModalDetails
          position={position}
          profitPercent={profitPercent}
          accountLeverage={accountLeverage}
          marginBalance={marginBalance}
          marginError={marginError}
          isAddMargin={isAddMargin}
        />
      )}
      <SubmitButton
        buttonTheme={ButtonThemeEnum.NEUTRAL2}
        disabled={
          !(Number(marginAmountNum) > 0) || loading || Boolean(marginError)
        }
        onClick={onUpdateMargin}
      >
        {loading ? (
          <Spinner />
        ) : isAddMargin ? (
          t("add_margin")
        ) : (
          t("remove_margin")
        )}
      </SubmitButton>
    </BaseDetailsModal>
  );
}

export default AdjustIsolatedMarginModal;
